import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelTokenSource } from 'axios'
import Axios from 'axios'
import { inject, injectable } from 'inversify'
import type { BlobResponse, CreatedEntityData, TmAxiosResponse, Transport } from '@/services/transport/types'
import { httpBuildQuerySerializer } from '@/utils/url'
import { SERVICE_TYPES } from '@/core/container/types'
import type { Config } from '@/core/types'

@injectable()
export class HttpService implements Transport<AxiosRequestConfig> {
  protected source: AxiosInstance = Axios.create()

  private cache: Map<string, Promise<TmAxiosResponse<unknown>>> = new Map()

  constructor(@inject(SERVICE_TYPES.Config) private readonly _config: Config) {}

  public getAxios(): AxiosInstance {
    return this.source
  }

  public getCancelToken(): CancelTokenSource {
    return Axios.CancelToken.source()
  }

  public create(config?: AxiosRequestConfig) {
    this.source = Axios.create(this.config(config))
  }

  public request<T>(config: AxiosRequestConfig): Promise<TmAxiosResponse<T>> {
    return this.source.request(this.config(config))
  }

  public get<T = unknown, P = Record<string, any>>(
    url: string,
    config?: AxiosRequestConfig<P>,
  ): Promise<TmAxiosResponse<T>> {
    const cacheKey = this.getUri(url, config)

    if (!this.cache.has(cacheKey)) {
      this.cache.set(
        cacheKey,
        this.source.get<T>(url, this.config(config)).finally(() => {
          this.cache.delete(cacheKey)
        }),
      )
    }

    return this.cache.get(cacheKey) as Promise<TmAxiosResponse<T>>
  }

  public async getFile<P extends AxiosRequestConfig = AxiosRequestConfig>(
    url: string,
    config?: P,
  ): Promise<BlobResponse> {
    const response = await this.get<Blob>(url, { ...config, responseType: 'blob' })
    return response as BlobResponse
  }

  public delete<T>(url: string, config?: AxiosRequestConfig): Promise<TmAxiosResponse<T>> {
    return this.source.delete(url, this.config(config))
  }

  public head<T>(url: string, config?: AxiosRequestConfig): Promise<TmAxiosResponse<T>> {
    return this.source.head(url, this.config(config))
  }

  public post<R = CreatedEntityData, T = unknown>(
    url: string,
    data?: T,
    _config?: AxiosRequestConfig,
  ): Promise<TmAxiosResponse<R>> {
    const config = this.config(_config, !this.hasFile(data))
    return this.source.post(url, data, config)
  }

  public put<T, B = Record<string, any>>(
    url: string,
    data?: B,
    config?: AxiosRequestConfig,
  ): Promise<TmAxiosResponse<T>> {
    return this.source.put(url, data, this.config(config))
  }

  public patch<T, B = Record<string, any>>(
    url: string,
    data?: B,
    config?: AxiosRequestConfig,
  ): Promise<TmAxiosResponse<T>> {
    return this.source.patch(url, data, this.config(config))
  }

  public success<T>(response: AxiosResponse<T>): T {
    return response.data
  }

  public constructSearchQueryObject(
    endpoint: string,
    fields: Array<string>,
    value: string,
  ): Promise<TmAxiosResponse<unknown>> {
    const filters = {
      searchQuery: value,
      searchFields: fields,
    }
    return this.get(endpoint, {
      params: filters,
      paramsSerializer: (params) => httpBuildQuerySerializer(params),
    })
  }

  public getUri<C extends AxiosRequestConfig = AxiosRequestConfig>(url: string, config?: C): string {
    return Axios.getUri({ url, ...this.config(config) })
  }

  protected config(config?: AxiosRequestConfig, setDefaultTimeout = true): AxiosRequestConfig {
    return {
      timeout: setDefaultTimeout ? 60_000 : undefined,
      ...(config || {}),
      baseURL: this._config.apiUrl,
      withCredentials: true,
    }
  }

  protected hasFile(data: unknown): boolean {
    if (data instanceof FormData) {
      for (const value of data.values()) {
        const isBlob = value instanceof Blob
        const isFile = value instanceof File
        if (isBlob || isFile) {
          return true
        }
      }
    }
    return false
  }
}
